import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Divider,
} from '@material-ui/core';
import useGetDropdownValues from '../Dockets/CustomHooks/useGetDropdownValues';
import { Autocomplete } from '@mui/material';
import { toLocalISO } from '../GenTask/components/SharedFormFields';

const customerOptions = ['Worrell Harvesting', 'Laois Sawmill', 'Anthony Hyde - Hrs', 'Willy Fenton - Hrs', 'New', 'Other'];

const JobDocketFormModal = ({ open, onClose, onSave, initialData, spListObj, spList }) => {
  const defaultState = {
    driverName: '',
    truck: '',
    trailer: '',
    startKm: '',
    endKm: '',
    totalKm: 0,
    dateAndTime: toLocalISO(),
    CustomerForOtherWork: '',
    CustomerForOtherWorkOther: '',
    loaderMachineTruck: '',
    fromForestSP: '',
    toForestSP: '',
    jobStartTime: '',
    jobEndTime: '',
    nonChargeableTime: 0,
    totalChargeableTime: '',
    notesForWHOffice: '',
    notesCustomer: '',
  };

  console.log('SP LIST ===', spListObj);
  const [formData, setFormData] = useState(defaultState);

  const trucks = useGetDropdownValues('Truck');
  const trailers = useGetDropdownValues('Trailer');
  const loadMachine = useGetDropdownValues('LoaderMachineTruck');
  const { driverList: drivers } = useGetDropdownValues('driver');

  useEffect(() => {
    if (initialData) {
      console.log('initial Data ===', initialData);
      setFormData(initialData);
    } else {
      setFormData(defaultState);
    }
  }, [initialData, open]);

  // KM Calculation
  const handleKmChange = (field, value) => {
    const next = { ...formData, [field]: value };
    const start = parseFloat(next.startKm) || 0;
    const end = parseFloat(next.endKm) || 0;
    next.totalKm = Math.max(0, end - start);
    setFormData(next);
  };

  // Helpers
  const timeToMinutes = time => {
    if (!time) return 0;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const minutesToHHMM = mins => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  useEffect(() => {
    const start = timeToMinutes(formData.jobStartTime);
    const end = timeToMinutes(formData.jobEndTime);
    const nonCharge = timeToMinutes(formData.nonChargeableTime) || 0;

    if (start && end && end > start) {
      const total = end - start - nonCharge;
      setFormData(prev => ({
        ...prev,
        totalChargeableTime: minutesToHHMM(Math.max(0, total)),
      }));
    }
  }, [formData.jobStartTime, formData.jobEndTime, formData.nonChargeableTime]);

  const renderOptions = list =>
    list?.map((item, idx) => (
      <MenuItem key={item.label} value={item.label}>
        {item.label}
      </MenuItem>
    ));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Edit Job Docket' : 'Add Job Docket'}</DialogTitle>

      <DialogContent dividers>
        {/* Driver & Vehicle */}
        <Typography variant="h6" color="primary" gutterBottom>
          Driver & Vehicle Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Driver Name*"
              select
              fullWidth
              variant="outlined"
              size="small"
              value={formData.driverName}
              onChange={e => setFormData({ ...formData, driverName: e.target.value })}>
              {renderOptions(drivers)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Truck*"
              select
              fullWidth
              variant="outlined"
              size="small"
              value={formData.truck}
              onChange={e => setFormData({ ...formData, truck: e.target.value })}>
              {renderOptions(trucks)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Trailer*"
              select
              fullWidth
              variant="outlined"
              size="small"
              value={formData.trailer}
              onChange={e => setFormData({ ...formData, trailer: e.target.value })}>
              {renderOptions(trailers)}
            </TextField>
          </Grid>
        </Grid>

        <Box my={3}>
          <Divider />
        </Box>

        {/* Chipper Info */}
        <Typography variant="h6" color="primary" gutterBottom>
          Chipper Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Start KM*"
              type="number"
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ min: 0 }}
              value={formData.startKm}
              onChange={e => handleKmChange('startKm', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="End KM*"
              type="number"
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ min: 0 }}
              value={formData.endKm}
              onChange={e => handleKmChange('endKm', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Total KM" fullWidth variant="outlined" size="small" disabled value={formData.totalKm} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Date & Time*"
              type="datetime-local"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.dateAndTime ? toLocalISO(formData.dateAndTime) : ''}
              onChange={e =>
                setFormData({
                  ...formData,
                  dateAndTime: toLocalISO(e.target.value),
                })
              }
            />
          </Grid>
        </Grid>

        <Box my={3}>
          <Divider />
        </Box>

        {/* Loader */}
        <Typography variant="h6" color="primary" gutterBottom>
          Load Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Loader Machine / Truck*"
              select
              fullWidth
              variant="outlined"
              size="small"
              value={formData.loaderMachineTruck}
              onChange={e => setFormData({ ...formData, loaderMachineTruck: e.target.value })}>
              {renderOptions(loadMachine)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Customer for Other Work*"
              select
              fullWidth
              variant="outlined"
              size="small"
              value={formData.CustomerForOtherWork}
              onChange={e =>
                setFormData({
                  ...formData,
                  CustomerForOtherWork: e.target.value,
                  CustomerForOtherWorkOther: '',
                })
              }>
              <MenuItem value="">Please Select</MenuItem>

              {customerOptions.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {['New', 'Other'].includes(formData.CustomerForOtherWork) && (
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Enter Customer Name*"
                fullWidth
                variant="outlined"
                size="small"
                value={formData.CustomerForOtherWorkOther}
                onChange={e =>
                  setFormData({
                    ...formData,
                    CustomerForOtherWorkOther: e.target.value,
                  })
                }
              />
            </Grid>
          )}

          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={spList || []}
              getOptionLabel={option => option.label || ''}
              value={spList?.find(opt => opt.value === formData.fromForestSP) || null}
              onChange={(e, newValue) =>
                setFormData({
                  ...formData,
                  fromForestSP: newValue?.value || '',
                })
              }
              renderInput={params => <TextField {...params} label="From Forest SP" variant="outlined" size="small" />}
              sx={{
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={spList || []}
              getOptionLabel={option => option.label || ''}
              value={spList?.find(opt => opt.value === formData.toForestSP) || null}
              onChange={(e, newValue) =>
                setFormData({
                  ...formData,
                  toForestSP: newValue?.value || '',
                })
              }
              renderInput={params => <TextField {...params} label="To Forest SP" variant="outlined" size="small" />}
              sx={{
                '& .MuiInputBase-root': {
                  height: 40,
                },
              }}
            />
          </Grid>
        </Grid>

        <Box my={3}>
          <Divider />
        </Box>

        {/* Time */}
        <Typography variant="h6" color="primary" gutterBottom>
          Job Start and End Time
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Start Time"
              type="time"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.jobStartTime}
              onChange={e => setFormData({ ...formData, jobStartTime: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="End Time"
              type="time"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.jobEndTime}
              onChange={e => setFormData({ ...formData, jobEndTime: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Non-Chargeable Time"
              type="time"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.nonChargeableTime}
              onChange={e => setFormData({ ...formData, nonChargeableTime: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Total Chargeable Time"
              fullWidth
              variant="outlined"
              size="small"
              disabled
              value={formData.totalChargeableTime}
            />
          </Grid>
        </Grid>

        <Box my={3}>
          <Divider />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={() => onSave(formData)}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDocketFormModal;
